import { FocusTrap, FocusTrapFeatures } from '@headlessui/react';
import { Trans, useLingui } from '@lingui/react/macro';
import * as React from 'react';
import DialogShell from 'components/DialogShell';
import { isOptionAvailable } from 'utils/auth/dealerIdOptions';
import { getDealerWindowMessage } from 'utils/auth/dealerSelectionMessages';
import type { IDealerSelectionDialogProps } from 'utils/auth/dealerResolution.types';
import DealerIdPicker from './DealerIdPicker';

const FOCUS_TRAP_FEATURES =
  FocusTrapFeatures.InitialFocus |
  FocusTrapFeatures.TabLock |
  FocusTrapFeatures.FocusLock |
  FocusTrapFeatures.RestoreFocus;

type TNeutralStyle = React.CSSProperties & Record<`--ex3d-${string}`, string>;

const NEUTRAL_FONT_FAMILY = 'var(--groupui-vwgroup-ref-font-family-the-group-text)';

/**
 * Neutral look of the Dealer ID window, whatever `brand-X` class the host carries. The dialog tokens are
 * read by DialogShell's panel and backdrop (and the picker's OK button), so this must sit on an ancestor
 * of DialogShell. The font tokens are set too because brand themes override them. Other dialogs keep
 * their themed look.
 */
const NEUTRAL_STYLE: TNeutralStyle = {
  '--ex3d-dialog-bg-color': 'var(--ex3d-dealer-white)',
  '--ex3d-dialog-text-color': 'var(--ex3d-dealer-gray-900)',
  '--ex3d-dialog-backdrop-color': 'var(--groupui-vwgroup-ref-color-deep-space-blue-900-80)',
  '--ex3d-dialog-border-radius': '0.5rem',
  '--ex3d-dialog-confirm-bg-color': 'var(--ex3d-dealer-primary)',
  '--ex3d-dialog-confirm-text-color': 'var(--ex3d-dealer-white)',
  '--ex3d-dialog-font-family': NEUTRAL_FONT_FAMILY,
  '--ex3d-font-family-primary': NEUTRAL_FONT_FAMILY,
  '--ex3d-font-family-secondary': NEUTRAL_FONT_FAMILY,
};

/**
 * Blocking dealer selection step (AVS-4405): one Combobox, OK, one red message line, no intro or switch
 * hint. Rendered only by `SecureOutlet` while the dealer gate is pending a choice: no close control, no
 * Escape handling, no backdrop click-through — the only way out is OK.
 */
export default function DealerSelectionDialog({
  options,
  brand,
  market,
  error,
  preselectedDealerId,
  onSelect,
}: IDealerSelectionDialogProps) {
  const { i18n } = useLingui();
  const message = getDealerWindowMessage({ options, error, brand, market });

  // FocusTrap needs a stable `HTMLElement` ref regardless of which element it ends up pointing at;
  // the picker forwards each DOM node here through a callback ref.
  const okButtonRef = React.useRef<HTMLElement | null>(null);
  const searchInputRef = React.useRef<HTMLElement | null>(null);

  const initialFocus = isOptionAvailable(options, preselectedDealerId) ? okButtonRef : searchInputRef;

  return (
    <FocusTrap features={FOCUS_TRAP_FEATURES} initialFocus={initialFocus}>
      <div data-testid="dealer-selection-neutral-theme" style={NEUTRAL_STYLE}>
        <DialogShell
          panelProps={{
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': 'dealer-selection-dialog-title',
            ...(message ? { 'aria-describedby': 'dealer-selection-error' } : {}),
          }}
          panelTestId="dealer-selection-dialog"
          size="compact"
          anchor="top"
        >
          <h2
            part="dialog-title"
            id="dealer-selection-dialog-title"
            className="text-[18px] font-semibold"
            data-testid="dealer-selection-dialog-title"
          >
            <Trans>Choose your Dealer ID</Trans>
          </h2>
          {message && (
            <p
              id="dealer-selection-error"
              role="alert"
              part="dialog-message"
              data-testid="dealer-selection-error"
              className="mt-2.5 text-sm text-red-700"
            >
              {i18n._(message)}
            </p>
          )}
          <DealerIdPicker
            options={options}
            preselectedDealerId={preselectedDealerId}
            onSelect={onSelect}
            searchInputRef={(element) => {
              searchInputRef.current = element;
            }}
            okButtonRef={(element) => {
              okButtonRef.current = element;
            }}
          />
        </DialogShell>
      </div>
    </FocusTrap>
  );
}
